<template>
  <div class="pay-result" v-loading="pageLoading">
    <div class="result-container" v-if="!pageLoading">
      <!-- 信息图标 -->
      <div class="info-icon">
        <img :src="statusInfo.icon" alt="info" />
      </div>

      <!-- 状态信息 -->
      <div class="status-info">
        <h2 class="status-title">{{ statusInfo.title }}</h2>
        <p class="status-desc" v-if="statusInfo.desc">{{ statusInfo.desc || reason }}</p>
      </div>

      <!-- 订单列表按钮 -->
      <div class="action-buttons">
        <es-button
          :variant="payStatus === 'success' ? 'base' : 'outline'"
          :theme="payStatus === 'success' ? 'primary' : ''"
          @click="goToOrderList">
          {{ $t('订单列表') }}
        </es-button>
        <es-button v-if="payStatus === 'success'" variant="outline" @click="jumpDetail">
          {{ hasElecInvoice ? $t('获取发票') : $t('开发票') }}
        </es-button>
        <es-button @click="goBack" v-if="returnUrl" variant="outline">
          {{ $t('返回') }}
        </es-button>
      </div>

      <!-- 汇款账户信息 -->
      <RemittanceInfoCard
        v-if="payStatus === 'confirming'"
        :bankPayInfo="bankPayInfo"
        class="remittance-info-card"
        :loadingStatus="false"
        :payAmountDisplay="payAmountDisplay" />
      <!-- 支付成功引导添加官方客服微信 -->
      <div class="official-weichat-guide" v-if="payStatus === 'success' && sourceChannel === 28">
        <div class="guide-title">{{ $t('微信扫码添加官方客服微信') }}</div>
        <div class="guide-desc">
          <img src="@/assets/icons/gift-icon.png" alt="gift-icon" />
          <span>{{ $t('首次添加可赠送证据报告一份') }}</span>
        </div>
        <div class="guide-qrcode">
          <img src="@/assets/images/qywx.png" :alt="$t('企业微信')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { $t } from '@/locales'
import RemittanceInfoCard from '@/components/RemittanceInfoCard'
import { PAYMENT_METHOD } from '@/consts/payment'

export default {
  name: 'PayResult',
  components: {
    RemittanceInfoCard,
  },
  beforeRouteEnter(to, from, next) {
    if (to.query.payOrderId || to.query.paymentMethod === PAYMENT_METHOD.BANK) {
      next()
    } else {
      next('/MyOrder')
    }
  },
  data() {
    return {
      bankPayInfo: {},
      isDeliver: false,
      hasElecInvoice: false,
      status: 'pending',
      clock: null,
      reason: '',
      orderList: [],
      money: '',
      returnUrl: localStorage.getItem('returnUrl') || '',
      sourceChannel: 0,
      orderPayStatus: '',
      orderInfo: {}, // 订单详情数据
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo,
      isOrg: state => state.isOrg,
      mixScene: state => state.mixScene,
      isPartner: state => state.isPartner,
      showOrderBtn: state => state.showOrderBtn,
    }),
    statusMap() {
      return {
        success: {
          icon: require('@/assets/icons/pay-success.svg'),
          title: $t('支付成功'),
          desc: this.isDeliver ? $t('预计1-2周发出') : '',
        },
        confirming: {
          icon: require('@/assets/icons/confirming.svg'),
          title: $t('转账结果确认中'),
          desc: $t('汇款成功后，订单将在10分钟内生效，请耐心等待'),
        },
        fail: {
          icon: require('@/assets/icons/pay-fail.svg'),
          title: $t('支付失败'),
          desc: '',
        },
      }
    },
    payStatus() {
      if (this.$route.query.paymentMethod === PAYMENT_METHOD.BANK) {
        if (this.orderPayStatus === 1) {
          return 'success'
        }
        return 'confirming'
      }
      if (this.status === 'resolve') {
        return 'success'
      }

      return 'fail'
    },
    statusInfo() {
      return this.statusMap[this.payStatus]
    },
    pageLoading() {
      return this.status === 'pending'
    },
    amount() {
      return Number(this.orderInfo?.price - (this.orderInfo?.amountPaid || 0)) || 0
    },
    payAmountDisplay() {
      return this.amount.toFixed(2)
    },
  },
  async mounted() {
    this.ouid = localStorage.getItem('ouid')
    this.init()
    if (this.$route.query.orderId && this.$route.query.invoiceId) {
      this.$ajax(this.$url.invoicesSimpleinfo(this.$route.query.invoiceId)).then(res => {
        if (res.data && res.data[0].invoiceForm === 1) {
          this.hasElecInvoice = true
        }
      })
    }
    if (this.$route.query.paymentMethod === PAYMENT_METHOD.BANK) {
      this.queryBankInfo()
      if (this.$route.query.orderId) await this.queryOrderDetail()
      this.status = 'resolve'
    }
  },
  methods: {
    queryBankInfo() {
      this.$ajax(this.$url.createbankAccountNoPay(this.$route.query.orderId), {}).then(res => {
        this.bankPayInfo = res.data || {}
      })
    },
    goToOrderList() {
      this.$router.push('/MyOrder')
    },
    getSourceChannel() {
      this.sourceChannel = Number(window.sessionStorage.getItem('source_channel')) || 0
    },
    uniteGoBack() {
      window.close()
    },
    goBack() {
      // 融合生态的场景
      if (this.mixScene) {
        this.uniteGoBack(this.mixScene)

        return
      }
      const url = localStorage.getItem('returnUrl') || ''
      const isContainParams = url.indexOf('?') === -1
      window.location.href = decodeURIComponent(`${url}${isContainParams ? '?' : '&'}orderId=${this.$route.query.orderId}`)
    },
    init() {
      this.getSourceChannel()
      this.status = 'pending'
      const { payOrderId } = this.$route.query
      if (payOrderId) {
        this.clock = setInterval(() => {
          this.$ajax(this.$url.getPayOrderDetail(payOrderId)).then(res => {
            if (res.data.status === 'PAY_SUCCESS') {
              this.status = 'resolve'
              clearInterval(this.clock)
              this.$reportBusiness('business_monitor_event_success', 'order_pay', {
                appId: this.$route.query.appid,
                ouid: this.userInfo?.gid,
                orgId: this.userInfo?.orgId,
                payOrderId,
                payStatus: res.data.status,
                stage: 'result',
              })
            } else if (res.data.status === 'PAY_AUDITING' || res.data.status === 'PAY_PENDING') {
              this.status = 'pending'
            } else {
              this.$reportBusiness('business_monitor_event_fail', 'order_pay', {
                appId: this.$route.query.appid,
                ouid: this.userInfo?.gid,
                orgId: this.userInfo?.orgId,
                payOrderId,
                payStatus: res.data.status,
                stage: 'result',
              })
              const { status } = res.data
              if (status === 'PAY_OUT_TIME') {
                this.reason = $t('用户取消')
              } else if (status === 'PAY_CANCEL') {
                this.reason = $t('超时取消')
              } else if (status === 'PAY_ABEND') {
                this.reason = $t('异常终止')
              } else {
                this.reason = res.data.status
              }
              this.status = 'reject'
              clearInterval(this.clock)
            }
          })
        }, 1000)
        if (this.$route.query.orderId) {
          this.$ajax(this.$url.getOrderdetail(), {
            payOrderId,
          }).then(res => {
            this.orderList = res.data.orders
            this.isDeliver = res.data.isDeliver
            let money = 0
            this.orderList.forEach(element => {
              money += element.price
            })
            this.money = money
          })
        }
      }
    },
    async queryOrderDetail() {
      const oid = this.userInfo.orgId ? this.userInfo.orgId : this.ouid
      await this.$ajax(this.$url.getOrderDetailByOrderId(this.$route.query.orderId, oid), {}).then(async res => {
        this.orderPayStatus = res.data.payStatus
        this.orderList = res.data.orders // 订单列表，当前场景不展示
        this.orderInfo = res.data // 订单详情数据
      })
    },
    jumpToEle() {
      this.$router.push({
        path: '/ElecInvoice',
        query: {
          orderId: this.$route.query.orderId,
        },
      })
    },
    // 开发票
    async jumpDetail() {
      if (this.hasElecInvoice) {
        this.jumpToEle()
      } else {
        await this.checkPriceAdjusting()
        this.jumpInvoice()
      }
    },
    // 我要开票
    jumpInvoice() {
      window.sessionStorage.orderList = JSON.stringify(this.orderList)
      this.$router.push({
        path: '/MyOrder/invoice',
        query: {
          type: 1,
        },
      })
    },
    // 查询订单是否在改价中
    async checkPriceAdjusting() {
      try {
        const { data } = await this.$ajax(this.$url.checkPriceAdjusting(this.$route.query.orderId))
        if (data) {
          this.$message.error($t('账单修改中，不支持操作'))

          return new Promise(() => {})
        }
      } catch (_) {}
    },
  },
}
</script>

<style lang="less" scoped>
.pay-result {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.result-container {
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  padding: 120px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.info-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
  }
}

.status-info {
  text-align: center;
  margin-bottom: 24px;
}

.status-title {
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  color: #333;
  margin-bottom: 8px;
}

.status-desc {
  font-size: 14px;
  color: #666;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
}

.action-buttons {
  margin-bottom: 32px;
}
.remittance-info-card {
  width: 640px;
  background-color: #fafafa;
}
.official-weichat-guide {
  padding: 24px 20px;
  background-color: #fafafa;
  border-radius: 4px;
  width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  .guide-title {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #666;
  }
  .guide-desc {
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    background: linear-gradient(90deg, #ff9f5d 0%, #ff7a88 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
    display: flex;
    align-items: center;
    margin-top: 6px;
    img {
      width: 20px;
      height: 20px;
      margin-right: 6px;
    }
  }
  .guide-qrcode {
    margin-top: 14px;
    img {
      width: 118px;
      height: 118px;
    }
  }
}
</style>
